
import { useMemo, useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { PRESETS, formatVa } from '@/lib/power'
import { zeroReasonText, type BuiltPowerPlan, type PowerPlanItem, type PowerPlanSnapshot, type PowerRow } from '@/lib/power-plan'
import { btn, card, cx, inputSm, tag, tagTone } from '@/lib/ui'
import type { PowerDevicePick } from './PowerPlanClient'

type Confidence = 'known' | 'estimated' | 'unknown'

const MCB_OPTIONS = [6, 10, 16, 20, 25, 32, 40, 50, 63]
const LEGS = ['L1', 'L2', 'L3']

/**
 * The schedule: circuits, the gear on them, and what each one pulls.
 *
 * Editable cells are inputs and derived cells are grey text, without exception.
 * You can see which numbers you asserted and which the plan worked out, which
 * is the difference between a schedule somebody will sign and a spreadsheet
 * nobody trusts.
 */
export function PowerScheduleTable({
  snapshot,
  built,
  devices,
  deviceDetail,
  confidenceFilter,
  showCircuits,
  onChange,
}: {
  snapshot: PowerPlanSnapshot
  built: BuiltPowerPlan
  devices: PowerDevicePick[]
  deviceDetail: Map<string, PowerDevicePick>
  confidenceFilter: Confidence | null
  /** Circuit grouping is an electrician's view; off, this is just a gear list. */
  showCircuits: boolean
  onChange: (next: (current: PowerPlanSnapshot) => PowerPlanSnapshot) => void
}) {
  const rowById = useMemo(() => new Map(built.rows.map((row) => [row.id, row])), [built.rows])

  const matches = (row: PowerRow | undefined) => {
    if (!confidenceFilter || !row) return true
    if (confidenceFilter === 'known') return row.provenance === 'PUBLISHED'
    if (confidenceFilter === 'estimated')
      return row.provenance === 'DERIVED_FROM_PSU' || row.provenance === 'PRESET'
    return row.zeroReason === 'UNKNOWN_DRAW'
  }

  const addGroup = () =>
    onChange((current) => ({
      ...current,
      groups: [
        ...current.groups,
        {
          id: `g-${current.groups.length + 1}-${current.groups.length}`,
          name: `Circuit ${current.groups.length + 1}`,
          breakerA: 16,
          leg: null,
          diversity: 1,
        },
      ],
    }))

  const updateGroup = (id: string, patch: Partial<PowerPlanSnapshot['groups'][number]>) =>
    onChange((current) => ({
      ...current,
      groups: current.groups.map((group) => (group.id === id ? { ...group, ...patch } : group)),
    }))

  const removeGroup = (id: string) =>
    onChange((current) => ({
      ...current,
      groups: current.groups.filter((group) => group.id !== id),
      // The gear does not vanish with its circuit — it falls back to unassigned,
      // where it is still counted in the plan total and flagged as unplaced.
      items: current.items.map((item) => ({
        ...item,
        groupIds: item.groupIds.filter((groupId) => groupId !== id),
      })),
    }))

  const updateItem = (id: string, patch: Partial<PowerPlanItem>) =>
    onChange((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }))

  const removeItem = (id: string) =>
    onChange((current) => ({ ...current, items: current.items.filter((item) => item.id !== id) }))

  const addItem = (item: PowerPlanItem) =>
    onChange((current) => ({ ...current, items: [...current.items, item] }))

  const unassignedItems = snapshot.items.filter((item) => item.groupIds.length === 0)

  // The default: everything on the show as one list, because "what am I
  // bringing" is a list and not a panel schedule.
  if (!showCircuits) {
    return (
      <section className={cx(card, 'overflow-hidden')} aria-label="Power schedule">
        <header className="flex items-center justify-between gap-3 border-b border-[var(--border)] px-5 py-3.5">
          <h2 className="text-sm font-medium">Gear on this show</h2>
          <span className="text-[11px] text-muted">
            {snapshot.items.length} {snapshot.items.length === 1 ? 'item' : 'items'}
          </span>
        </header>
        <ItemRows
          items={snapshot.items.filter((item) => matches(rowById.get(item.id)))}
          rowById={rowById}
          deviceDetail={deviceDetail}
          groups={snapshot.groups}
          showCircuits={false}
          onUpdate={updateItem}
          onRemove={removeItem}
        />
        <AddGear devices={devices} contextLabel="the plan" onAdd={(item) => addItem(item)} />
      </section>
    )
  }

  return (
    <section className={cx(card, 'overflow-hidden')} aria-label="Power schedule">
      <header className="flex items-center justify-between gap-3 border-b border-[var(--border)] px-5 py-3.5">
        <h2 className="text-sm font-medium">Schedule</h2>
        <button type="button" onClick={addGroup} className={cx(btn.sm, btn.secondary)}>
          <Icon name="plus" className="h-3.5 w-3.5" strokeWidth={2} />
          Add circuit
        </button>
      </header>

      {built.groups.map((group) => {
        const settingsGroup = snapshot.groups.find((candidate) => candidate.id === group.id)
        const items = snapshot.items.filter((item) => item.groupIds.includes(group.id))
        return (
          <div key={group.id} className="border-b border-[var(--border)] last:border-b-0">
            <div className="flex flex-wrap items-center gap-2 bg-[var(--surface-raised)]/60 px-5 py-2.5">
              <input
                aria-label={`Circuit name ${group.name}`}
                value={settingsGroup?.name ?? group.name}
                onChange={(event) => updateGroup(group.id, { name: event.target.value })}
                className={cx(inputSm, 'w-44 font-medium')}
              />
              <select
                aria-label={`Leg for ${group.name}`}
                className={inputSm}
                value={settingsGroup?.leg ?? ''}
                onChange={(event) => updateGroup(group.id, { leg: event.target.value || null })}
              >
                <option value="">single phase</option>
                {LEGS.map((leg) => (
                  <option key={leg} value={leg}>
                    {leg}
                  </option>
                ))}
              </select>
              <select
                aria-label={`Breaker for ${group.name}`}
                className={inputSm}
                value={settingsGroup?.breakerA ?? ''}
                onChange={(event) =>
                  updateGroup(group.id, { breakerA: event.target.value ? Number(event.target.value) : null })
                }
              >
                <option value="">no breaker set</option>
                {MCB_OPTIONS.map((amps) => (
                  <option key={amps} value={amps}>
                    MCB {amps} A
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-[11px] text-muted">
                diversity
                <input
                  aria-label={`Diversity for ${group.name}`}
                  type="number"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={settingsGroup?.diversity ?? 1}
                  onChange={(event) =>
                    updateGroup(group.id, { diversity: Math.min(1, Math.max(0.1, Number(event.target.value))) })
                  }
                  className={cx(inputSm, 'w-16 tabular-nums')}
                />
              </label>
              <button
                type="button"
                onClick={() => removeGroup(group.id)}
                aria-label={`Remove circuit ${group.name}`}
                className={cx(btn.sm, btn.ghost, 'ml-auto')}
              >
                <Icon name="trash" className="h-3.5 w-3.5" />
              </button>
            </div>

            <p
              className={cx(
                'px-5 py-1.5 text-[11px] tabular-nums',
                group.over ? 'bg-danger-soft text-danger' : 'text-muted',
              )}
            >
              {formatVa(group.vaConnected)} · load {group.ampsConnected.toFixed(2)} A
              {group.ampsFailover > group.ampsConnected &&
                ` · on-failure ${group.ampsFailover.toFixed(2)} A`}
              {group.usableA != null && ` · usable ${group.usableA.toFixed(1)} A`}
              {group.utilisationPct != null && ` · ${group.utilisationPct.toFixed(0)}%`}
              {group.outlets > 0 && ` · ${group.outlets} outlets`}
            </p>
            {group.warnings.map((warning) => (
              <p key={warning} className="px-5 pb-1.5 text-[11px] text-danger">
                {warning}
              </p>
            ))}

            <ItemRows
              items={items.filter((item) => matches(rowById.get(item.id)))}
              rowById={rowById}
              deviceDetail={deviceDetail}
              groups={snapshot.groups}
              showCircuits
              onUpdate={updateItem}
              onRemove={removeItem}
            />
            <AddGear
              devices={devices}
              contextLabel={group.name}
              onAdd={(item) => addItem({ ...item, groupIds: [group.id] })}
            />
          </div>
        )
      })}

      {/* Always present when rows have no circuit. Excluding a known row because
          it is unplaced makes the total silently low; putting it in a circuit it
          is not on makes that circuit wrong. A third bucket is the honest one. */}
      <div className="border-t border-[var(--border)]">
        <div className="flex items-center gap-2 bg-[var(--surface-raised)]/40 px-5 py-2.5">
          <p className="text-sm font-medium text-muted">Not on a circuit</p>
          <span className={cx(tag, unassignedItems.length > 0 ? tagTone.warning : tagTone.neutral)}>
            {unassignedItems.length} {unassignedItems.length === 1 ? 'row' : 'rows'}
          </span>
          <p className="text-[11px] text-muted">
            counted in the plan total, on no circuit
          </p>
        </div>
        <ItemRows
          items={unassignedItems.filter((item) => matches(rowById.get(item.id)))}
          rowById={rowById}
          deviceDetail={deviceDetail}
          groups={snapshot.groups}
          showCircuits
          onUpdate={updateItem}
          onRemove={removeItem}
        />
        <AddGear devices={devices} contextLabel="the plan" onAdd={(item) => addItem(item)} />
      </div>
    </section>
  )
}

function ItemRows({
  items,
  rowById,
  deviceDetail,
  groups,
  showCircuits,
  onUpdate,
  onRemove,
}: {
  items: PowerPlanItem[]
  rowById: Map<string, PowerRow>
  deviceDetail: Map<string, PowerDevicePick>
  groups: PowerPlanSnapshot['groups']
  showCircuits: boolean
  onUpdate: (id: string, patch: Partial<PowerPlanItem>) => void
  onRemove: (id: string) => void
}) {
  if (items.length === 0) {
    return <p className="px-5 py-3 text-[11px] text-muted">Nothing here yet.</p>
  }
  return (
    <ul>
      {items.map((item) => {
        const row = rowById.get(item.id)
        const device = item.deviceId ? deviceDetail.get(item.deviceId) : undefined
        const missingDevice = Boolean(item.deviceId) && !device
        const excluded = Boolean(row?.zeroReason)
        return (
          <li
            key={item.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-[var(--border)] px-5 py-2 first:border-t-0"
          >
            <input
              aria-label={`Quantity for ${item.label}`}
              type="number"
              min={1}
              max={999}
              value={item.qty}
              onChange={(event) => onUpdate(item.id, { qty: Math.max(1, Number(event.target.value) || 1) })}
              className={cx(inputSm, 'w-14 tabular-nums')}
            />

            <div className="min-w-0 flex-1">
              <p className={cx('truncate text-sm', excluded && 'text-muted line-through')}>
                {item.label}
              </p>
              <p className="truncate text-[11px] text-muted">
                {missingDevice
                  ? 'no longer in the catalogue — the figure below is the one this plan saved'
                  : (row?.detail ?? '')}
                {device?.powerNote ? ` · ${device.powerNote}` : ''}
              </p>
            </div>

            {/* W each: an input, because it is an assertion. Null is an amber
                dash you can tab to, never a zero that joins the total. */}
            <label className="flex items-center gap-1">
              <span className="sr-only">Watts for {item.label}</span>
              <input
                type="number"
                min={0}
                max={100000}
                value={item.wattsMax ?? ''}
                placeholder={row?.wattsMax != null ? String(Math.round(row.wattsMax)) : '—'}
                onChange={(event) => {
                  const value = event.target.value
                  onUpdate(item.id, {
                    wattsMax: value === '' ? null : Number(value),
                    powerSource: item.powerSource ?? null,
                  })
                }}
                className={cx(
                  inputSm,
                  'w-20 tabular-nums',
                  row?.zeroReason === 'UNKNOWN_DRAW' &&
                    'border-dashed border-warning-line placeholder:text-warning',
                )}
              />
              <span className="text-[11px] text-muted">W</span>
            </label>

            {/* Power factor, VA and amps are how the total is arrived at, not
                what anyone brings to a venue. They belong to the electrician's
                view. */}
            {showCircuits && (
              <>
                <span className="w-12 text-right text-[11px] tabular-nums text-muted" title="power factor">
                  {row ? row.powerFactor.toFixed(2) : '—'}
                  {row && item.powerFactor == null && <sup aria-hidden>·</sup>}
                </span>
                <span className="w-20 text-right text-[11px] tabular-nums text-muted">
                  {row?.vaConnected != null ? formatVa(row.vaConnected) : '—'}
                </span>
                <span className="w-16 text-right text-[11px] tabular-nums text-muted">
                  {row?.ampsConnected != null ? `${row.ampsConnected.toFixed(2)} A` : '—'}
                </span>
              </>
            )}

            {showCircuits && (
              <span className="w-16 text-[11px] text-muted" title="mains cords">
                {row ? (row.psuMode === 'REDUNDANT' ? `${row.psuCount} (A+B)` : row.psuCount) : '—'}
              </span>
            )}

            {/* Where the watts went. A non-mains row is struck through above and
                names its host here, so you can watch the load move rather than
                trust that it did. */}
            {/* Silent when a row is just plugged into the wall — the column
                only earns its width when the watts went somewhere else. */}
            {(() => {
              // Rendering it empty left a 160px span that wrapped to a blank
              // line under every row on a phone. A column that has nothing to
              // say should take no space at all.
              const attribution = row?.zeroReason
                ? `${zeroReasonText(row.zeroReason)}${row.attributedTo ? ` → ${row.attributedTo}` : ''}`
                : showCircuits
                  ? 'mains'
                  : ''
              if (!attribution) return null
              return (
                <span className="truncate text-[11px] text-muted sm:w-40">{attribution}</span>
              )
            })()}

            {showCircuits && (
              <select
                aria-label={`Circuit for ${item.label}`}
                className={inputSm}
                value={item.groupIds[0] ?? ''}
                onChange={(event) =>
                  onUpdate(item.id, { groupIds: event.target.value ? [event.target.value] : [] })
                }
              >
                <option value="">no circuit</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            )}

            <button
              type="button"
              onClick={() => onRemove(item.id)}
              aria-label={`Remove ${item.label}`}
              className={cx(btn.sm, btn.ghost)}
            >
              <Icon name="trash" className="h-3.5 w-3.5" />
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * One field doing three jobs: a catalogue device, a preset for the gear ROUTR
 * does not model (LED wall, moving light, HVAC), or free text for a box nobody
 * has specced yet. Typing something unknown must never be a dead end.
 */
function AddGear({
  devices,
  contextLabel,
  onAdd,
}: {
  devices: PowerDevicePick[]
  contextLabel: string
  onAdd: (item: PowerPlanItem) => void
}) {
  const [text, setText] = useState('')
  const listId = `gear-${contextLabel.replace(/\W+/g, '-').toLowerCase()}`

  const submit = () => {
    const needle = text.trim()
    if (!needle) return
    const id = `i-${needle.replace(/\W+/g, '').slice(0, 8)}-${devices.length}-${needle.length}`

    const device = devices.find(
      (candidate) => `${candidate.manufacturer} ${candidate.model}`.toLowerCase() === needle.toLowerCase(),
    )
    if (device) {
      onAdd({
        id,
        kind: 'DEVICE',
        deviceId: device.id,
        label: `${device.manufacturer} ${device.model}`,
        qty: 1,
        groupIds: [],
        catalogueWattsAtSnapshot: device.powerDrawMaxW,
      })
      setText('')
      return
    }

    const preset = PRESETS.find((candidate) => candidate.label.toLowerCase() === needle.toLowerCase())
    if (preset) {
      onAdd({ id, kind: 'PRESET', presetKey: preset.key, label: preset.label, qty: 1, groupIds: [] })
      setText('')
      return
    }

    // Free text: no figure, so it lands as an explicit unknown rather than a
    // zero. The gaps panel will name it.
    onAdd({ id, kind: 'MANUAL', label: needle, qty: 1, groupIds: [] })
    setText('')
  }

  return (
    <div className="px-5 py-2.5">
      <input
        aria-label={`Add gear to ${contextLabel}`}
        list={listId}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            submit()
          }
        }}
        onBlur={submit}
        placeholder="Add gear…"
        className={cx(inputSm, 'w-full max-w-sm')}
      />
      <datalist id={listId}>
        {devices.map((device) => (
          <option key={device.id} value={`${device.manufacturer} ${device.model}`} />
        ))}
        {PRESETS.map((preset) => (
          <option key={preset.key} value={preset.label} />
        ))}
      </datalist>
    </div>
  )
}
